import React from 'react';
import './index.scss';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { selectFilteredServices, services } from '../../../AppSlice';
import { ArrowLeft, ArrowRight, Launch } from '@carbon/icons-react';
import { getAvailableProductTools, getProduct, products, ToolId } from '../../../config/products';
import oneGuiImage from '../../../assets/images/homepage/OneGui.svg';
import transcodeImage from '../../../assets/images/homepage/xTranscode.svg';
import ingestImage from '../../../assets/images/homepage/xIngest.svg';
import serverImage from '../../../assets/images/homepage/Server.svg';
import compRecImage from '../../../assets/images/homepage/CompRec.svg';
import processImage from '../../../assets/images/homepage/PROcess.svg';
import exchangeImage from '../../../assets/images/homepage/PROexchange.svg';

interface HomeProps {
    selectedProductId: string | null;
    onSelectProduct: (productId: string) => void;
    onClearProduct: () => void;
}

const productImages: Record<string, string> = {
    'OneGui.svg': oneGuiImage,
    'xTranscode.svg': transcodeImage,
    'xIngest.svg': ingestImage,
    'Server.svg': serverImage,
    'CompRec.svg': compRecImage,
    'PROcess.svg': processImage,
    'PROexchange.svg': exchangeImage,
};

const Home: React.FC<HomeProps> = ({ selectedProductId, onSelectProduct, onClearProduct }) => {
    const navigate = useNavigate();
    const availableTools = useAppSelector(selectFilteredServices) as ToolId[];
    const selectedProduct = getProduct(selectedProductId);
    const selectedTools = getAvailableProductTools(selectedProduct, availableTools);

    const selectProduct = (productId: string) => {
        const product = getProduct(productId);
        if (!product) return;
        if (product.externalUrl) {
            window.open(product.externalUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        onSelectProduct(product.id);
    };

    return (<main className="home-page">
        <header className="home-heading">
            <h1>x-dream Media Suite &amp; Broadcast Suite</h1>
            {!selectedProduct && <p>Please select a product:</p>}
        </header>

        {!selectedProduct && <section className="product-grid" aria-label="Products">
            {products.map(product => {
                const image = product.image ? productImages[product.image] : undefined;
                return (
                    <button
                        key={product.id}
                        className={`product-tile product-tile--${product.id}`}
                        onClick={() => selectProduct(product.id)}
                    >
                        {image
                            ? <img src={image} alt={product.name} />
                            : <span className="product-tile__wordmark">{product.name}</span>}
                        {product.externalUrl && <Launch className="product-tile__launch" size={18} aria-label="Opens in a new tab" />}
                    </button>
                );
            })}
        </section>}

        {selectedProduct && <section className="product-workspace" aria-label={`${selectedProduct.name} workspace`}>
            <button className="product-workspace__back" onClick={onClearProduct}>
                <ArrowLeft size={18} /> Back to products
            </button>
            <div className="product-workspace__heading">
                <span>Selected product</span>
                <h2>{selectedProduct.name}</h2>
                <p>Select a tool to continue.</p>
            </div>
            <div className="related-tools" aria-label={`${selectedProduct.name} tools`}>
                {selectedTools.map(toolId => {
                    const ToolIcon = services[toolId].icon;
                    return (
                        <button key={toolId} onClick={() => navigate(`/service/${toolId}`)}>
                            <ToolIcon size={26} />
                            <span>{services[toolId].name}</span>
                            <ArrowRight size={20} />
                        </button>
                    );
                })}
                {selectedTools.length === 0 && <span className="related-tools__empty">No related tools are available for your account.</span>}
            </div>
        </section>}
    </main>);
};

export default Home;
